import { RouteComponentProps } from '@reach/router';
import Button from 'components/Button';
import notify from 'components/Notify';
import Select from 'components/Select';
import { IOption } from 'components/Select/Select';
import {
  BadgeContainer,
  ContentContainer,
  ItemRow,
  ItemsWrapper,
} from 'containers/HomePage/styles';
import TaskFileExplorerModal from 'containers/Tasks/TasksManagement/TaskFileExplorerModal';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  syncDownStreamConfig,
  updateCompanyDownStream,
} from 'store/companies/companiesSlice';
import {
  IDownStreamConfig,
  IDownStreamTarget,
} from 'store/companies/companiesTypes';
import { requireProviderTags } from 'store/providers/providerSlice';
import { IProviderItemView, ITagView } from 'store/providers/providerTypes';
import { appDispatch, RootState } from 'store/store';
import styled from 'styled-components';
import LocalIcon from 'ui/LocalIcon';
import { black, orange, red } from 'utils/style/colors';
import Collapsible from '../../../../components/Collapsible';

interface ISubCompany {
  companyId?: number;
}

type IDownStreamState = IDownStreamConfig & { editing?: boolean };

const SubCompanyContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.5rem;
  height: 100%;
`;

const FolderHeader = styled.h5`
  font-weight: bold;
  color: ${black};
  padding-inline-start: 0.25rem;
`;

const FolderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-start;
  flex-wrap: wrap;
`;

const SubCompany: React.FC<ISubCompany & RouteComponentProps> = ({
  companyId,
}) => {
  const { t } = useTranslation('companyManagement');

  const providers = useSelector(
    (state: RootState) => state.companies.data?.[companyId!].relatedProviders
  );

  const tags = useSelector((state: RootState) => state.providers.tags);
  const companies = useSelector((state: RootState) => state.companies.data);

  const [saving, setSaving] = useState<boolean>(false);
  const [syncing, setSyncing] = useState<number>(0); // --> 0 : not syncing , ID: syncing downStream with this ID

  const [showExplorerModal, setShowExplorerModal] = useState(false);

  const streamingConfig = useSelector(
    (state: RootState) =>
      state.companies.data[Number(companyId)]?.streamingConfig
  );

  const [groupOptions, setGroupOptions] = useState<IOption[] | undefined>(() =>
    streamingConfig?.groups?.map((group) => ({
      label: group.name,
      value: group.id,
    }))
  );

  const [downStreamConfigOptions, setDownStreamConfigOptions] = useState<
    IDownStreamState[] | undefined
  >(() => {
    return streamingConfig?.downStreams;
  });

  const [targetItems, setTargetItems] = useState<IDownStreamTarget[]>(
    () => streamingConfig?.targets?.filter((target) => !target.deleted) || []
  );

  const [selectedProviderItems, setSelectedProviderItems] = useState<
    IProviderItemView[]
  >(() => {
    return (streamingConfig?.downStreams?.map((downStream) => ({
      id: downStream.sourceFolderId,
      title: downStream.sourceFolderName,
    })) ?? []) as IProviderItemView[];
  });

  useEffect(() => {
    Object.keys(providers).map((pid) => {
      if (!Object.keys(tags || {}).includes(pid)) {
        appDispatch(requireProviderTags({ providerId: +pid }));
      }
    });
  }, []);

  const handleImportItemFromClipboard = () => {
    window.navigator.clipboard
      .readText()
      .then((data) => {
        const copiedItem = JSON.parse(data);
        setTargetItems((prevState) => [
          ...prevState,
          {
            targetFolderName: copiedItem.name,
            companyName: companies?.[copiedItem.companyId]?.name || '',
            companyId: copiedItem.companyId,
            providerId: copiedItem.providerId,
            id: copiedItem.sourceItemId,
            targetFolderId: copiedItem.sourceItemId,
          } as IDownStreamTarget,
        ]);
      })
      .catch(() => {
        notify(t('nothingCopiedInClipboard'), 'error');
      });
  };

  const handleRemoveTargets = (id: string) => {
    setTargetItems((prevState) =>
      prevState.filter((target) => target.id !== id)
    );
  };

  const handleRemoveDownStream = (id: number) => {
    setDownStreamConfigOptions((prevState = []) => {
      return prevState.filter((downStream) => downStream.sourceFolderId !== id);
    });
    setSelectedProviderItems((prevState = []) =>
      prevState.filter((item) => item.id !== id)
    );
  };

  const handleChangeProviderItem = (
    relatedProviderItem: IProviderItemView | null
  ) => {
    const slectedProviderItemsId = selectedProviderItems.map((el) => el.id);
    if (
      relatedProviderItem?.id &&
      slectedProviderItemsId.includes(relatedProviderItem.id)
    ) {
      setSelectedProviderItems((prevState) =>
        prevState.reduce((acc: IProviderItemView[], el) => {
          if (el.id === relatedProviderItem.id) acc.push(relatedProviderItem);
          else acc.push(el);
          return acc;
        }, [])
      );
    } else if (relatedProviderItem?.id) {
      setSelectedProviderItems((prevState) => [
        ...prevState,
        relatedProviderItem,
      ]);
    }

    const downStreamIdList = downStreamConfigOptions?.map(
      (stream) => stream.sourceFolderId
    );
    if (!(downStreamIdList || [])?.includes(relatedProviderItem!.id))
      setDownStreamConfigOptions((prevState) => [
        ...(prevState || []),
        {
          id: Math.random().toString(),
          sourceFolderId: relatedProviderItem!.id,
          sourceFolderName:
            relatedProviderItem!.name || relatedProviderItem!.title || '',
          targetGroups: [],
          providerId: relatedProviderItem!.providerId,
          editing: true,
        },
      ]);
    setShowExplorerModal(false);
  };

  const handleSaveSubCompany = () => {
    setSaving(true);
    const groups = (groupOptions || []).map((el) => ({
      id: el.value.toString(),
      name: el.value.toString(),
    }));
    const streamingConfig = {
      groups: groups,
      targets: targetItems,
      downStreams: downStreamConfigOptions || [],
    };
    appDispatch(
      updateCompanyDownStream({ companyId: companyId!, streamingConfig })
    )
      .then(() => {
        setSaving(false);
        setDownStreamConfigOptions(
          (downStreamConfigOptions || []).map(
            (el) => ({ ...el, editing: false }) as IDownStreamState
          )
        );
        notify(t('savedSuccessfully'));
      })
      .catch(() => {
        notify(t('failedToSaved'), 'error');
        setSaving(false);
      });
  };

  const handleSync = (id: number) => {
    if (!id) {
      notify(t('needToSaveYourConfigBeforeSyncing'));
    } else {
      setSyncing(id);
      if (companyId) {
        appDispatch(
          syncDownStreamConfig({ companyId, downStreamConfigId: id })
        );
      }
    }
  };

  const generateBadgedItem = (name: string) => {
    return (
      <>
        <BadgeContainer color={orange}>
          <LocalIcon iconName={'zip.svg'} />
        </BadgeContainer>
        <ContentContainer>
          <div
            style={{
              position: 'relative',
              top: '0',
              overflow: 'hidden',
              textAlign: 'center',
            }}
          >
            {name}
          </div>
        </ContentContainer>
      </>
    );
  };

  // const tagOptions =

  return (
    <SubCompanyContainer>
      <Select
        label={t('subCompanyGroups')}
        options={groupOptions || []}
        values={groupOptions || []}
        onChangeValues={(values: IOption[]) => {
          setGroupOptions(() => [...values]);
        }}
        letAddChip={true}
        isRequired={true}
        allowRemove={true}
        multiChoice={true}
      />
      <Collapsible
        customHeaderStyle={{ width: '100%' }}
        header={
          <div className="flex justify-between full-width align-center">
            <FolderHeader>{t('refrencedFolders')}</FolderHeader>{' '}
            <div className="flex justify-end">
              <Button
                color="black-outline"
                onClick={handleImportItemFromClipboard}
              >
                {t('addFolder', { ns: 'companyManagement' })}
              </Button>
            </div>
          </div>
        }
      >
        <div>
          <FolderContainer>
            {targetItems.map((target) => {
              const tagOptions = Object.keys(providers)
                .reduce((current, pid) => {
                  return [
                    ...current,
                    ...Object.values(tags?.[Number(pid)]?.data || {}),
                  ];
                }, [] as ITagView[])
                .filter((tag) => !tag.deleted)
                .map((tag) => ({ label: tag.text, value: tag.id }));
              return (
                <ItemRow
                  key={`${target.providerId}-${target.companyId}-${target.id}`}
                >
                  <div className="flex items-center gap-1 mx-2">
                    <LocalIcon
                      iconName="x.svg"
                      color={red}
                      onClick={() => handleRemoveTargets(target.id)}
                      isLink={true}
                    />
                    <ItemsWrapper
                      onClick={() => {
                        const urlItem = `/content/${target.providerId}/f/${target.id}`;
                        const win = window.open(urlItem, '_blank');
                        win?.focus();
                      }}
                      style={{ margin: '0 0.5rem' }}
                    >
                      {generateBadgedItem(
                        `${target.companyName}/${target.targetFolderName}`
                      )}
                    </ItemsWrapper>
                  </div>

                  <div style={{ maxWidth: '40rem' }}>
                    <Select
                      options={tagOptions || []}
                      values={tagOptions.filter((tag) =>
                        target.tagIds?.includes(Number(tag.value))
                      )}
                      onChangeValues={(values) => {
                        setTargetItems((prevState) =>
                          (prevState ?? []).map((el) =>
                            el.targetFolderId === target.targetFolderId
                              ? {
                                  ...el,
                                  tagIds: values.map((tagOption) =>
                                    Number(tagOption.value)
                                  ),
                                }
                              : el
                          )
                        );
                      }}
                      label={t('applyTagsToThisTarget')}
                      multiChoice={true}
                    />
                  </div>

                  <div style={{ maxWidth: '30rem' }}>
                    <Select
                      options={groupOptions || []}
                      values={groupOptions?.filter(
                        (o) =>
                          target.groups?.includes(o.value?.toString()) ?? false
                      )}
                      label={t('groupsItBelongsTo')}
                      onChangeValues={(values) => {
                        setTargetItems((prevState) =>
                          (prevState ?? []).map((t) =>
                            t.id === target.id
                              ? {
                                  ...t,
                                  groups: values.map((v) => String(v.value)),
                                }
                              : t
                          )
                        );
                      }}
                      multiChoice={true}
                    />
                  </div>
                </ItemRow>
              );
            })}
          </FolderContainer>
        </div>
      </Collapsible>

      {showExplorerModal && (
        <TaskFileExplorerModal
          providers={providers}
          onSave={handleChangeProviderItem}
          onClose={() => setShowExplorerModal((prevState) => !prevState)}
        />
      )}
      <Collapsible
        customHeaderStyle={{ width: '100%' }}
        header={
          <div className="flex justify-between full-width align-center">
            <FolderHeader>{t('downStreamFolders')}</FolderHeader>
            <div className="flex justify-end">
              <Button
                color="black-outline"
                onClick={() => setShowExplorerModal((prevState) => !prevState)}
              >
                {t('addDownStream')}
              </Button>
            </div>
          </div>
        }
      >
        <div>
          <FolderContainer>
            {selectedProviderItems?.map((el: IProviderItemView) => {
              const foundedDownStream = (downStreamConfigOptions || []).find(
                (downStream) => downStream.sourceFolderId === el.id
              );
              return (
                <div
                  className="flex items-center gap-1 mx-2 w-full"
                  key={`${el.providerId}-${companyId}-${el.id}`}
                >
                  <LocalIcon
                    iconName="x.svg"
                    color={red}
                    onClick={() => handleRemoveDownStream(el.id)}
                    isLink={true}
                  />
                  <ItemRow>
                    <ItemsWrapper
                      onClick={() => {
                        // const urlItem = `/content/${el.providerId}/f/${el.id}`;
                        // const win = window.open(urlItem, '_blank');
                        // win?.focus();
                      }}
                      style={{ margin: '0 0.5rem' }}
                    >
                      {generateBadgedItem(el?.name || el?.title || '')}
                    </ItemsWrapper>

                    {foundedDownStream && (
                      <div className="flex-1 flex justify-end gap-2">
                        <div style={{ maxWidth: '30rem' }}>
                          <Select
                            options={groupOptions || []}
                            label={t('streamToGroups')}
                            values={groupOptions?.filter((o) =>
                              foundedDownStream.targetGroups.includes(
                                o.value.toString()
                              )
                            )}
                            onChangeValues={(values) => {
                              setDownStreamConfigOptions((prevState) =>
                                (prevState ?? []).map((downStream) =>
                                  downStream.sourceFolderId === el.id
                                    ? {
                                        ...downStream,
                                        targetGroups: values.map((v) =>
                                          String(v.value)
                                        ),
                                      }
                                    : downStream
                                )
                              );
                            }}
                            multiChoice={true}
                          />
                        </div>
                        <div className="flex items-center">
                          <Button
                            color="black-outline"
                            loading={syncing === el.id}
                            onClick={() =>
                              handleSync(Number(foundedDownStream.id))
                            }
                            disabled={foundedDownStream.editing}
                          >
                            {t('sync')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </ItemRow>
                </div>
              );
            })}
          </FolderContainer>
        </div>
      </Collapsible>
      <Button
        type="button"
        color="black-outline"
        onClick={handleSaveSubCompany}
        loading={saving}
      >
        {t('save')}
      </Button>
    </SubCompanyContainer>
  );
};

export default SubCompany;
